import { useState, useEffect, useMemo } from 'react';

// Class-dropdown data scoped to the signed-in user. Teacher / Surveillant /
// Principal are limited to the classes they're assigned (via /academic/my-scope);
// GlobalAdmin and any unscoped role see the full catalog. Returns the ordered
// visible class list plus a managed `className` that always stays valid for it.
// The section dropdowns are already scoped server-side by /academic/sections.
export default function useScopedClasses(apiCall, year) {
  const [classLevels, setClassLevels] = useState([]);
  const [scopedClasses, setScopedClasses] = useState(null); // null = unscoped (see all); Set = only these
  const [className, setClassName] = useState('');

  useEffect(() => {
    apiCall('/registration/class-levels').then(d => setClassLevels(d || [])).catch(() => {});
  }, [apiCall]);

  useEffect(() => {
    apiCall(`/academic/my-scope?academicYear=${year}`)
      .then(s => setScopedClasses(s && s.scoped ? new Set(s.classNames || []) : null))
      .catch(() => setScopedClasses(null));
  }, [apiCall, year]);

  // Full catalog (ordered), narrowed to the user's scope. Any scoped class not
  // in the catalog is still shown, so the user never loses one of their classes.
  const visibleClasses = useMemo(() => {
    if (!scopedClasses) return classLevels;
    const inCatalog = classLevels.filter(cl => scopedClasses.has(cl.ClassName));
    const known = new Set(inCatalog.map(c => c.ClassName));
    const extra = [...scopedClasses].filter(n => !known.has(n)).map(n => ({ ClassName: n }));
    return [...inCatalog, ...extra];
  }, [classLevels, scopedClasses]);

  // Keep the selection valid for the visible set (default to the first class).
  useEffect(() => {
    if (!visibleClasses.length) { setClassName(''); return; }
    setClassName(prev => visibleClasses.some(c => c.ClassName === prev) ? prev : visibleClasses[0].ClassName);
  }, [visibleClasses]);

  return { classLevels, visibleClasses, className, setClassName };
}
